import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, CheckCircle, Clock, FileText, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const WorkmanshipWarrantyPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Our 5-Year Workmanship Warranty | Carolina Trophy Roofs</title>
        <meta name="description" content="Learn about Carolina Trophy Roofs' comprehensive 5-year workmanship warranty and warranty expansion packages. Understand what's covered, how to make claims, and upgrade options." />
        <meta name="keywords" content="roofing workmanship warranty, 5 year warranty, warranty expansion, roofing guarantee, warranty coverage, North Carolina roofing warranty" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/workmanship-warranty" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">Our Workmanship Warranty</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Shield className="mr-2 h-4 w-4" />
              Warranty Information
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Our 5-Year Workmanship Warranty
            </h1>
            <p className="text-xl text-gray-600">
              Comprehensive protection with expansion options available
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8">
              <h2 className="text-xl font-semibold text-green-800 mb-2">What Makes Our Warranty Different</h2>
              <p className="text-green-700">
                Carolina Trophy Roofs provides a comprehensive <strong>5-year workmanship warranty</strong> on all installations, 
                with <strong>warranty expansion packages available</strong> for extended protection. This commitment reflects our 
                confidence in our certified technicians and quality materials.
              </p>
            </div>

            <h2 className="text-2xl font-bold mb-4">Standard 5-Year Coverage Details</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <CheckCircle className="h-8 w-8 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-3">Installation Defects</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Improper shingle installation</li>
                  <li>• Flashing installation errors</li>
                  <li>• Underlayment application issues</li>
                  <li>• Ventilation system problems</li>
                  <li>• Gutter and downspout defects</li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg border">
                <Clock className="h-8 w-8 text-blue-500 mb-4" />
                <h3 className="text-lg font-semibold mb-3">Leak Protection</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Water infiltration repairs</li>
                  <li>• Emergency leak response</li>
                  <li>• Interior damage assessment</li>
                  <li>• Temporary weatherization</li>
                  <li>• Complete leak remediation</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Warranty Expansion Packages</h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <div className="flex items-center mb-3">
                <Star className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-blue-800">Extended Protection Options</h3>
              </div>
              <p className="text-blue-700 mb-4">
                Upgrade your warranty coverage with our expansion packages for even greater peace of mind:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">10-Year Extended Warranty</h4>
                  <p className="text-sm text-gray-600">Double your coverage period with our premium extension package</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Premium Plus Package</h4>
                  <p className="text-sm text-gray-600">Includes enhanced coverage and priority service response</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Industry Standards & Compliance</h2>
            <p className="mb-4">
              Our warranty terms align with recommendations from the <a href="https://www.nrca.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">National Roofing Contractors Association (NRCA)</a> and exceed standards set by the <a href="https://www.roofingalliance.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Roofing Alliance</a>. According to the <a href="https://www.rci-online.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">RCI (Roof Consultants Institute)</a>, comprehensive workmanship warranties with expansion options demonstrate contractor confidence and provide superior customer protection.
            </p>

            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-3">Warranty Registration Process</h3>
              <ol className="space-y-2">
                <li><strong>1. Automatic Registration:</strong> All warranties are automatically registered upon project completion</li>
                <li><strong>2. Documentation Package:</strong> You'll receive warranty certificates, installation photos, and material specifications</li>
                <li><strong>3. Digital Records:</strong> Warranty information is stored in our customer database for easy access</li>
                <li><strong>4. Expansion Options:</strong> We'll discuss available warranty expansion packages during your consultation</li>
              </ol>
            </div>

            <h2 className="text-2xl font-bold mb-4">Making a Warranty Claim</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">1</span>
                </div>
                <h3 className="font-semibold mb-2">Contact Us</h3>
                <p className="text-sm text-muted-foreground">
                  Call (252) 371-2266 or email claims@carolinatrophyroofs.com
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">2</span>
                </div>
                <h3 className="font-semibold mb-2">Inspection</h3>
                <p className="text-sm text-muted-foreground">
                  We'll schedule a thorough inspection within 48 hours
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">3</span>
                </div>
                <h3 className="font-semibold mb-2">Resolution</h3>
                <p className="text-sm text-muted-foreground">
                  Approved repairs are completed at no cost to you
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Maintaining Your Warranty Coverage</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Important Requirements</h3>
              <ul className="text-yellow-700 space-y-2">
                <li>• Schedule annual inspections (recommended but not required)</li>
                <li>• Keep gutters clean and properly maintained</li>
                <li>• Report any damage within 30 days of discovery</li>
                <li>• Use only authorized contractors for any roof modifications</li>
                <li>• Maintain proper attic ventilation</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold mb-4">Transfer and Coverage Details</h2>
            <p className="mb-4">
              Our workmanship warranty is <strong>transferable to new homeowners</strong> for the remaining term, 
              adding value to your property sale. Warranty expansion packages are also transferable, providing even 
              greater value. According to research by the <a href="https://www.realtor.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">National Association of Realtors</a>, 
              transferable roof warranties can increase home value and appeal to potential buyers.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-3">Coverage Exclusions</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Like all warranties, certain conditions are not covered:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Normal wear and tear</li>
                <li>• Acts of nature (covered by insurance)</li>
                <li>• Damage from walking on roof</li>
                <li>• Modifications by unauthorized contractors</li>
                <li>• Neglect or lack of maintenance</li>
              </ul>
            </div>

            <div className="text-center mt-12">
              <h2 className="text-2xl font-bold mb-4">Questions About Your Warranty?</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Our team is here to help you understand your warranty coverage, expansion options, and benefits.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/contact">Contact Us Today</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/resources">
                    <FileText className="mr-2 h-5 w-5" />
                    More Resources
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WorkmanshipWarrantyPage;
